/**
 * WorkspaceBus WS client · P2.6.a
 *
 * Connects to /ws/workspace/:workspace_id · subscribes to broadcast events ·
 * exposes a typed event stream + automatic reconnect with monotonic audit_id
 * gap detection (A24 · self-heal).
 *
 * Protocol:
 *   - Sec-WebSocket-Protocol: aiw.token.v1 + url-encoded SPA JWT
 *   - Frame: { kind, workspace_id, session_id?, audit_id?, payload?, ts }
 *   - Heartbeat: client sends `{type:'ping'}` every 30s · server echoes pong
 *   - Reconnect: exponential backoff (1s · 2s · 4s · ... cap 30s)
 *   - Resume: on reconnect · GET /api/audit-log?since_audit_id=N to fill gap
 *     before re-subscribing (A24 final-consistency)
 *
 * v0.1 scope: in-browser EventSource-style client · React hook on top of it.
 * v0.2 will add backpressure + pause for tab background · fragment streaming.
 *
 * codex-spike s6.1 (P0.2) validated full protocol (15/15) · we port the
 * essential pieces · keep skeleton simple.
 */

export interface WsEvent {
  kind: string;
  workspace_id: string;
  session_id?: string;
  audit_id?: number;
  payload?: Record<string, unknown>;
  ts: number;
}

export type WsState = 'idle' | 'connecting' | 'open' | 'reconnecting' | 'closed';

export interface WsClientOptions {
  backendUrl: string;        // https://api.aiw.dev
  workspaceId: string;
  /** SPA JWT · sent in Sec-WebSocket-Protocol per protocol.md §3 */
  token: string;
  /** Initial audit_id high-water mark · 0 if first connect. */
  sinceAuditId?: number;
  /** Test seam · default global WebSocket. */
  WebSocketImpl?: typeof WebSocket;
  /** Event handler · receives parsed frames. */
  onEvent?: (evt: WsEvent) => void;
  /** State change handler · for UI badges (connected/reconnecting). */
  onState?: (state: WsState) => void;
  /** Reconnect delays · default exponential 1s/2s/4s... cap 30s. */
  reconnectDelaysMs?: number[];
  /** Heartbeat interval · default 30s. */
  heartbeatMs?: number;
}

export class WorkspaceBusClient {
  private ws: WebSocket | null = null;
  private state: WsState = 'idle';
  private opts: WsClientOptions;
  private reconnectAttempt = 0;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private highWater = 0; // latest seen audit_id
  private explicitClose = false;
  /**
   * Monotonic socket generation. Incremented on every new socket. Listener
   * closures capture the gen at attach-time so a late close event from a
   * superseded socket can't trigger reconnect on the new one. Fixes codex R2
   * close/revive race · prior version had the late close handler call
   * `scheduleReconnect()` after a manual close→connect cycle had already
   * built a fresh socket.
   */
  private gen = 0;

  constructor(opts: WsClientOptions) {
    this.opts = opts;
    this.highWater = opts.sinceAuditId ?? 0;
  }

  private setState(s: WsState): void {
    this.state = s;
    this.opts.onState?.(s);
  }

  /** Open the WebSocket. Idempotent · returns early if a socket is already
   * open / connecting / a reconnect timer is pending.
   *
   * codex-review 2026-04-29 P2.6.a R1 P2 fix · prior version skipped only on
   * 'open'/'connecting' · but a pending reconnect timer would still create a
   * 2nd socket if user code called connect() manually. Now we also bail when
   * a timer is queued · and explicitly clear any pending timer when we're
   * actually about to connect (prevents racing the timer's deferred connect).
   */
  connect(): void {
    if (this.ws && (this.state === 'open' || this.state === 'connecting' || this.state === 'reconnecting')) return;
    if (this.reconnectTimer) {
      // A reconnect is already queued · let it fire · don't double-connect.
      return;
    }
    this.explicitClose = false;
    this.setState(this.reconnectAttempt === 0 ? 'connecting' : 'reconnecting');

    // Build URL · ws/wss + path
    const base = this.opts.backendUrl
      .replace(/^http:/, 'ws:')
      .replace(/^https:/, 'wss:')
      .replace(/\/$/, '');
    const url = `${base}/ws/workspace/${encodeURIComponent(this.opts.workspaceId)}`;

    const Ctor = this.opts.WebSocketImpl ?? globalThis.WebSocket;
    if (!Ctor) throw new Error('No WebSocket implementation available');

    // Subprotocol-based auth (per protocol.md + P1.7)
    const subprotocols = ['aiw.token.v1', encodeURIComponent(this.opts.token)];
    this.gen += 1;
    this.ws = new Ctor(url, subprotocols);
    this.attachListeners(this.gen);
  }

  /** Close the WebSocket · prevent reconnect. After close() the client is
   * idle and `connect()` may be called again to revive it (explicitClose
   * flips back to false on the next connect call · see connect()).
   *
   * Bumps gen so any late open/message/close events from the prior socket
   * are ignored after revival (codex R2 P2 fix).
   */
  close(): void {
    this.explicitClose = true;
    this.gen += 1; // invalidate any in-flight listeners
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.stopHeartbeat();
    if (this.ws) {
      try {
        this.ws.close(1000, 'client-close');
      } catch {
        // ignore
      }
      this.ws = null;
    }
    // Reset attempt counter so revived connect() starts at attempt 0
    this.reconnectAttempt = 0;
    this.setState('closed');
  }

  /** Latest audit_id seen · used for gap-detect REST replay on reconnect. */
  getHighWater(): number {
    return this.highWater;
  }

  private attachListeners(gen: number): void {
    if (!this.ws) return;
    const ws = this.ws;
    ws.addEventListener('open', () => {
      // Late open from a superseded socket · ignore.
      if (gen !== this.gen) return;
      this.reconnectAttempt = 0;
      this.setState('open');
      this.startHeartbeat();
    });
    ws.addEventListener('message', (e: MessageEvent) => {
      if (gen !== this.gen) return;
      const data = typeof e.data === 'string' ? e.data : '';
      if (!data) return;
      let frame: WsEvent | { type?: string };
      try {
        frame = JSON.parse(data);
      } catch {
        return;
      }
      // server-side heartbeat reply · ignore in event stream
      if ((frame as any).type === 'pong') return;
      const evt = frame as WsEvent;

      // codex-review 2026-04-29 P2.6.a R1 P1 fix · stricter validation prevents
      // a misrouted/poison broadcast from corrupting high-water (which would
      // make subsequent REST gap-fills miss real events) or from polluting the
      // audit store with wrong-workspace rows.
      if (typeof evt.kind !== 'string' || evt.kind.length === 0) return;
      if (evt.workspace_id !== this.opts.workspaceId) return; // cross-workspace · drop
      if (evt.audit_id !== undefined) {
        if (!Number.isSafeInteger(evt.audit_id) || evt.audit_id <= 0) return;
      }
      if (typeof evt.ts !== 'number' || !Number.isFinite(evt.ts)) return;

      if (typeof evt.audit_id === 'number' && evt.audit_id > this.highWater) {
        this.highWater = evt.audit_id;
      }
      this.opts.onEvent?.(evt);
    });
    ws.addEventListener('close', () => {
      // Late close from a superseded socket · don't trigger reconnect on
      // the new generation. (codex R2 P2 fix · prior version had the late
      // close fire scheduleReconnect on the wrong client state.)
      if (gen !== this.gen) return;
      this.stopHeartbeat();
      if (this.explicitClose) return;
      this.scheduleReconnect();
    });
    ws.addEventListener('error', () => {
      // close handler will fire next · reconnect there
    });
  }

  private startHeartbeat(): void {
    this.stopHeartbeat();
    const ms = this.opts.heartbeatMs ?? 30_000;
    this.heartbeatTimer = setInterval(() => {
      try {
        this.ws?.send(JSON.stringify({ type: 'ping' }));
      } catch {
        // socket dead · close handler will fire
      }
    }, ms);
  }

  private stopHeartbeat(): void {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  private scheduleReconnect(): void {
    const delays = this.opts.reconnectDelaysMs ?? [1000, 2000, 4000, 8000, 15000, 30000];
    const idx = Math.min(this.reconnectAttempt, delays.length - 1);
    const delay = delays[idx];
    this.reconnectAttempt++;
    this.setState('reconnecting');
    // Mark previous socket cleared · timer fires connect() which will gate
    // on `if (this.reconnectTimer) return` · we clear timer first inside
    // the callback below so the gate opens.
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.ws = null; // ensure connect() can build a fresh socket
      this.connect();
    }, delay);
  }
}

/**
 * REST helper · pull audit events with audit_id > since for gap recovery.
 *
 * Returns one page of events plus a `has_more` flag. workspace is sourced
 * from the JWT server-side · we DO NOT send `workspace_id` in the query
 * (codex-review 2026-04-29 P2.6.a R1 P2 fix · had been a copy-paste leftover
 * that suggested workspace was selectable client-side · A18 says no).
 *
 * Pagination: caller (`useWorkspaceBus`) loops while `has_more === true`,
 * passing the last seen `audit_id` as next `since_audit_id` and stopping at
 * a max page count to bound recovery work.
 */
export interface AuditPage {
  events: WsEvent[];
  has_more: boolean;
}

export async function fetchAuditSince(
  backendUrl: string,
  token: string,
  sinceAuditId: number,
  opts: { sessionId?: string; limit?: number } = {},
): Promise<AuditPage> {
  const params = new URLSearchParams();
  params.set('since_audit_id', String(sinceAuditId));
  if (opts.sessionId) params.set('session_id', opts.sessionId);
  if (opts.limit) params.set('limit', String(opts.limit));
  const url = `${backendUrl.replace(/\/$/, '')}/api/audit-log?${params.toString()}`;
  const resp = await fetch(url, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!resp.ok) {
    throw new Error(`audit-log fetch failed: ${resp.status}`);
  }
  const body = await resp.json() as { events?: WsEvent[]; has_more?: boolean };
  // Defensive validation · server must return events array (else treat as empty)
  const events = Array.isArray(body.events) ? body.events : [];
  return { events, has_more: body.has_more === true };
}
